// Site configuration for marketing pages
export const SITE = {
  name: "EchoNote",
  domain: process.env.REACT_APP_DOMAIN || "echonote.app",
  supportEmail: process.env.REACT_APP_SUPPORT_EMAIL || "support@echonote.app",
  socials: {
    x: process.env.REACT_APP_SOCIAL_X || "",
    linkedin: process.env.REACT_APP_SOCIAL_LINKEDIN || "",
  },
};

export const PRICING = {
  free: {
    name: "Free (Trial)",
    price: 0,
    minutes: 60,
    fileCapMB: 100,
    features: [
      "60 minutes total transcription credit",
      "Upload audio/video files",
      "AI summaries & action items",
      "1 workspace",
      "1 user",
      "100MB file size limit",
    ],
    limitations: [
      "No bot auto-join",
      "No priority queue",
    ],
  },
  plans: [
    {
      key: "starter",
      name: "Starter",
      price: 10,
      stripePriceId: process.env.REACT_APP_STRIPE_STARTER_PRICE_ID || "price_starter",
      minutes: 300,
      overage: 0.05,
      users: 3,
      orgs: 1,
      features: [
        "300 minutes / month",
        "Everything in Free, plus:",
        "Bot recording (Zoom, Teams)",
        "Meeting scheduler",
        "Email invite ingest",
        "Outlook & Google Calendar sync",
        "Up to 3 users",
        "1 organization",
        "Community & email support",
      ],
    },
    {
      key: "team",
      name: "Team",
      price: 49,
      stripePriceId: process.env.REACT_APP_STRIPE_TEAM_PRICE_ID || "price_team",
      minutes: 2000,
      overage: 0.04,
      users: "unlimited",
      orgs: "multi",
      features: [
        "2,000 minutes / month",
        "Everything in Starter, plus:",
        "Multi-organization admin",
        "SSO (roadmap)",
        "Priority processing",
        "Audit log",
        "Role-based access",
        "Email support + 2-day SLA",
      ],
    },
  ],
};

// Feature matrix for pricing page
export const FEATURE_MATRIX = [
  { name: "Upload files", free: true, starter: true, team: true },
  { name: "AI summaries & action items", free: true, starter: true, team: true },
  { name: "Bot recording (Zoom/Teams)", free: false, starter: true, team: true },
  { name: "Meeting scheduler", free: false, starter: true, team: true },
  { name: "Email invite ingest", free: false, starter: true, team: true },
  { name: "Outlook sync", free: false, starter: true, team: true },
  { name: "Google Calendar sync", free: false, starter: true, team: true },
  { name: "Real-time status", free: true, starter: true, team: true },
  { name: "Org isolation", free: true, starter: true, team: true },
  { name: "Multi-org admin", free: false, starter: false, team: true },
  { name: "Role-based access", free: false, starter: false, team: true },
  { name: "SSO (roadmap)", free: false, starter: false, team: true },
  { name: "Priority processing", free: false, starter: false, team: true },
  { name: "API access (roadmap)", free: false, starter: false, team: true },
  { name: "Support", free: "Community", starter: "Email", team: "Email + SLA" },
];

// FAQ data for main page
export const MAIN_FAQ = [
  {
    question: "Do you need admin consent for Outlook Calendar sync?",
    answer: "No, we use delegated permissions that only require user consent. Your IT admin doesn't need to approve anything.",
  },
  {
    question: "Do you support Zoom Cloud Recording?",
    answer: "Yes, our bot can join Zoom meetings in real-time and record them. You can also upload pre-recorded Zoom cloud recordings manually.",
  },
  {
    question: "What file types are supported?",
    answer: "We support audio files (MP3, WAV, M4A) and video files (MP4, AVI, MOV) up to 500MB in size.",
  },
  {
    question: "How does multi-organization work?",
    answer: "Each organization has its own isolated workspace. Users can only see meetings, transcripts, and data within their organization. Perfect for agencies managing multiple clients.",
  },
  {
    question: "Can I invite the bot via email?",
    answer: "Yes! Forward meeting invites to your dedicated email address and our system will automatically schedule the bot to join.",
  },
  {
    question: "What's the transcription accuracy?",
    answer: "We use high-quality speech-to-text models to deliver accurate transcriptions. Accuracy depends on audio quality, accents, and background noise.",
  },
  {
    question: "How are minutes calculated?",
    answer: "Minutes are based on the audio duration of your recording, not wall-clock time. A 45-minute meeting uses 45 minutes of your quota.",
  },
];

// FAQ data for pricing page
export const PRICING_FAQ = [
  {
    question: "What happens when I run out of minutes?",
    answer: "You can upgrade to a higher plan or purchase additional minutes. Free users will be prompted to upgrade. Paid plans have overage rates: $0.05/min for Starter, $0.04/min for Team.",
  },
  {
    question: "Can I add more users?",
    answer: "Starter plan includes up to 3 users. Team plan supports unlimited users within your organization.",
  },
  {
    question: "Can I change plans mid-cycle?",
    answer: "Yes, you can upgrade or downgrade at any time. Upgrades take effect immediately, and you'll be prorated. Downgrades take effect at the next billing cycle.",
  },
  {
    question: "What does 'multi-organization' mean?",
    answer: "Team plan allows you to manage multiple organizations from a single account. Each organization has isolated data and users.",
  },
  {
    question: "Do unused minutes roll over?",
    answer: "No, minutes reset each month. The Free plan is a one-time 60-minute credit that doesn't renew.",
  },
];

// Analytics configuration
export const ANALYTICS = {
  ga4MeasurementId: process.env.REACT_APP_GA4_MEASUREMENT_ID || "",
  googleAdsConversionId: process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID || "",
};
